import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Pencil, RefreshCw, Loader2, Upload, X,
} from "lucide-react";
import {
  saveChannelProfile,
  regenerateChannelProfile,
  uploadCharacterReferenceImage,
  deleteCharacterReferenceImage,
  deleteCharacter,
  addCharacter,
  renameCharacter,
} from "@/lib/api";

/**
 * Channel-scoped Character Profile editor.
 *
 * Renders the character cast for one channel: name, species,
 * coat/face/outfit/accessories, plus a per-character reference
 * image grid. Lets the user rename, add, remove, and upload
 * references — all of which feed straight into gpt-image-1's
 * /v1/images/edits endpoint for both scene image generation
 * AND the storyboard thumbnail render path.
 *
 * Used by:
 *   - StoryboardList.jsx (Channel Search analysis card)
 *   - ActiveChannelDetail.jsx (Phase 4 active channels)
 *
 * Identity of the channel is the YouTube @handle — all backend
 * mutations are keyed by `handle_key` (`channel_analyses` collection),
 * so any edit here is shared across Channel Search and Active
 * Channel views for the same channel.
 */
export default function CharacterProfileEditor({
  handle,
  initialVisuals,
  // Renders the prompt-block read-out + JSON editor + Regenerate
  // button. Active-channel pages set this to false because the
  // surrounding chrome already shows the analysis status.
  showAdvanced = true,
}) {
  const [visuals, setVisuals] = useState(initialVisuals || null);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setVisuals(initialVisuals || null);
    setDraft(JSON.stringify(initialVisuals || {}, null, 2));
  }, [initialVisuals]);

  const onRegenerate = async () => {
    if (!handle || regenerating) return;
    setRegenerating(true);
    try {
      const res = await regenerateChannelProfile(handle);
      setVisuals(res.profile);
      setDraft(JSON.stringify(res.profile, null, 2));
      setEditing(false);
      toast.success("Character profile regenerated from latest thumbnails");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "channel_not_analyzed_yet") {
        toast.error("Analyze this channel from Channel Search first to extract characters");
      } else {
        toast.error(detail || "Couldn't regenerate profile");
      }
    } finally {
      setRegenerating(false);
    }
  };

  const onSave = async () => {
    let parsed;
    try {
      parsed = JSON.parse(draft);
    } catch {
      toast.error("That's not valid JSON — fix syntax then save again");
      return;
    }
    setSaving(true);
    try {
      const res = await saveChannelProfile(handle, parsed);
      setVisuals(res.profile);
      setDraft(JSON.stringify(res.profile, null, 2));
      setEditing(false);
      toast.success("Profile saved — next 'Create Image' will use it");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  const characters = visuals?.characters || [];
  const promptBlock = visuals?.prompt_block || visuals?.image_prompt_prefix || "";

  return (
    <div
      data-testid="channel-character-profile"
      className="border-t border-zinc-900 pt-3 space-y-2"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="channel-profile-toggle"
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">
          // CHARACTER PROFILE {visuals?.edited_by_user ? "· EDITED" : ""}
        </div>
        <span className="font-mono text-[10px] text-zinc-500">
          {expanded ? "[hide]" : "[show]"}
        </span>
      </button>

      {expanded && !visuals && (
        <div className="text-[11px] text-zinc-500 italic space-y-2">
          <p>
            No character profile yet — click <span className="text-emerald-300">Regenerate from thumbnails</span> below to extract one from the channel&apos;s recent uploads.
          </p>
          <button
            type="button"
            data-testid="channel-profile-empty-regen"
            onClick={onRegenerate}
            disabled={regenerating || !handle}
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/40 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
          >
            {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {regenerating ? "Re-analyzing…" : "Regenerate from thumbnails"}
          </button>
        </div>
      )}

      {expanded && visuals && !editing && (
        <>
          {characters.length > 0 && (
            <div className="space-y-2">
              {characters.map((c, i) => (
                <CharacterCard
                  key={`${c.name}-${i}`}
                  idx={i}
                  character={c}
                  handle={handle}
                  visuals={visuals}
                  onChanged={(nextVisuals) => {
                    setVisuals(nextVisuals);
                    setDraft(JSON.stringify(nextVisuals, null, 2));
                  }}
                />
              ))}
            </div>
          )}
          <AddCharacterForm
            handle={handle}
            existingNames={(characters || []).map((c) => (c.name || "").toLowerCase())}
            onAdded={(newChar) => {
              const nextVisuals = {
                ...visuals,
                characters: [...(visuals.characters || []), newChar],
                named_characters: Array.from(new Set([
                  ...(visuals.named_characters || []),
                  newChar.name,
                ])),
                edited_by_user: true,
              };
              setVisuals(nextVisuals);
              setDraft(JSON.stringify(nextVisuals, null, 2));
            }}
          />
          {showAdvanced && promptBlock && (
            <div className="bg-black/60 border border-zinc-800 rounded-sm p-2">
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                Prompt block (injected into every Create Image)
              </div>
              <p
                data-testid="channel-profile-prompt-block"
                className="text-[11px] text-zinc-300 leading-relaxed whitespace-pre-wrap"
              >
                {promptBlock}
              </p>
            </div>
          )}
          {showAdvanced && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="channel-profile-edit-btn"
                onClick={() => setEditing(true)}
                className="font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-yellow-400/60 text-zinc-300 hover:text-yellow-300 px-3 py-1.5 rounded-sm transition-colors"
              >
                Edit JSON
              </button>
              <button
                type="button"
                data-testid="channel-profile-regenerate-btn"
                onClick={onRegenerate}
                disabled={regenerating}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/40 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
              >
                {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {regenerating ? "Re-analyzing…" : "Regenerate from thumbnails"}
              </button>
            </div>
          )}
          {showAdvanced && (
            <ReferenceImageQuickSet
              handle={handle}
              current={visuals.user_reference_image_url || ""}
              onSaved={(nextProfile) => {
                setVisuals(nextProfile);
                setDraft(JSON.stringify(nextProfile, null, 2));
              }}
            />
          )}
        </>
      )}

      {expanded && editing && (
        <div className="space-y-2">
          <textarea
            data-testid="channel-profile-json-editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={Math.min(28, Math.max(12, draft.split("\n").length + 2))}
            className="w-full bg-black border border-zinc-800 focus:border-emerald-400/60 focus:outline-none rounded-sm px-3 py-2 text-[11px] text-zinc-100 font-mono leading-relaxed"
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="channel-profile-save-btn"
              onClick={onSave}
              disabled={saving}
              className="font-mono text-[10px] uppercase tracking-[0.25em] bg-emerald-400 text-black hover:bg-emerald-300 font-black px-4 py-1.5 rounded-sm transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save profile"}
            </button>
            <button
              type="button"
              data-testid="channel-profile-cancel-btn"
              onClick={() => {
                setEditing(false);
                setDraft(JSON.stringify(visuals || {}, null, 2));
              }}
              className="font-mono text-[10px] uppercase tracking-[0.25em] border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-zinc-200 px-4 py-1.5 rounded-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function CharacterCard({ idx, character, handle, visuals, onChanged }) {
  const c = character || {};
  const refs = c.reference_images || [];
  const [removing, setRemoving] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(c.name || "");
  const [renaming, setRenaming] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingRef, setDeletingRef] = useState(null);
  const fileInputRef = useRef(null);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const resolveSrc = (u) => {
    if (!u) return u;
    if (u.startsWith("http://") || u.startsWith("https://")) return u;
    return `${apiBase}${u}`;
  };

  const replaceCharInVisuals = (next) => ({
    ...visuals,
    characters: (visuals?.characters || []).map((x, i) => (i === idx ? next : x)),
  });

  const onPickCharacterImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are accepted");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB per reference");
      return;
    }
    setUploadingImage(true);
    try {
      const res = await uploadCharacterReferenceImage(handle, c.name, file);
      onChanged?.(replaceCharInVisuals({
        ...c,
        reference_images: [...refs, res.url],
      }));
      toast.success(`Reference uploaded for ${c.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const onDeleteRef = async (url) => {
    setDeletingRef(url);
    try {
      await deleteCharacterReferenceImage(handle, c.name, url);
      onChanged?.(replaceCharInVisuals({
        ...c,
        reference_images: refs.filter((u) => u !== url),
      }));
      toast.success(`Reference removed from ${c.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove reference");
    } finally {
      setDeletingRef(null);
    }
  };

  const saveField = async (field, rawValue) => {
    let nextValue = rawValue;
    if (field === "accessories") {
      nextValue = rawValue
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 8);
    }
    const nextChar = { ...c, [field]: nextValue };
    const nextVisuals = replaceCharInVisuals(nextChar);
    try {
      const res = await saveChannelProfile(handle, nextVisuals);
      onChanged?.(res.profile);
      toast.success(`${c.name}'s ${field.replace(/_/g, " ")} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
      throw err;
    }
  };

  const onRename = async () => {
    const next = nameDraft.trim();
    if (!next) {
      toast.error("Name can't be empty");
      return;
    }
    if (next === c.name) {
      setEditingName(false);
      return;
    }
    setRenaming(true);
    try {
      await renameCharacter(handle, c.name, next);
      onChanged?.({
        ...visuals,
        characters: (visuals?.characters || []).map((x, i) =>
          i === idx ? { ...x, name: next } : x,
        ),
        named_characters: Array.from(new Set([
          ...((visuals?.named_characters || []).filter(
            (n) => (n || "").toLowerCase() !== (c.name || "").toLowerCase(),
          )),
          next,
        ])),
        edited_by_user: true,
      });
      toast.success(`Renamed to ${next}`);
      setEditingName(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        detail === "character_already_exists" ? "That name is already in the cast"
        : detail || "Couldn't rename character",
      );
    } finally {
      setRenaming(false);
    }
  };

  const onRemoveCharacter = async () => {
    if (!window.confirm(`Remove ${c.name} from this channel's profile? This deletes any per-character reference images they own.`)) return;
    setRemoving(true);
    try {
      await deleteCharacter(handle, c.name);
      onChanged?.({
        ...visuals,
        characters: (visuals?.characters || []).filter((_, i) => i !== idx),
      });
      toast.success(`${c.name} removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove character");
      setRemoving(false);
    }
  };

  return (
    <div
      data-testid={`channel-profile-char-${idx}`}
      className="border border-emerald-400/20 bg-emerald-400/5 rounded-sm p-3"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          {refs.length > 0 ? (
            <div
              data-testid={`char-${idx}-ref-tile`}
              className="relative w-24 h-24 border border-emerald-400/40 bg-black rounded-sm overflow-hidden group"
            >
              <img
                src={resolveSrc(refs[0])}
                alt={`${c.name} reference`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onDeleteRef(refs[0])}
                disabled={!!deletingRef}
                data-testid={`char-${idx}-ref-delete`}
                title={`Remove ${c.name}'s reference image`}
                aria-label="Remove reference"
                className="absolute top-1 right-1 w-5 h-5 inline-flex items-center justify-center bg-black/80 hover:bg-red-500 text-white rounded-sm transition-colors opacity-0 group-hover:opacity-100"
              >
                {deletingRef === refs[0]
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <X className="w-3 h-3" />}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              data-testid={`channel-profile-char-upload-${idx}`}
              title={`Upload a reference image of ${c.name}`}
              className="w-24 h-24 border border-dashed border-emerald-400/50 hover:border-emerald-300 bg-emerald-400/5 hover:bg-emerald-400/10 text-emerald-300 hover:text-emerald-100 rounded-sm inline-flex flex-col items-center justify-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50"
            >
              {uploadingImage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span>{uploadingImage ? "uploading…" : `+ image`}</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onPickCharacterImage}
            className="hidden"
          />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            {editingName ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                <input
                  data-testid={`channel-profile-rename-input-${idx}`}
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onRename();
                    if (e.key === "Escape") {
                      setEditingName(false);
                      setNameDraft(c.name || "");
                    }
                  }}
                  autoFocus
                  maxLength={60}
                  className="bg-black border border-emerald-400/40 rounded-sm px-2 py-1 text-sm text-zinc-100 focus:border-emerald-300 focus:outline-none w-44"
                />
                <button
                  type="button"
                  onClick={onRename}
                  disabled={renaming}
                  data-testid={`channel-profile-rename-save-${idx}`}
                  className="font-mono text-[9px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-2 py-0.5 rounded-sm transition-colors disabled:opacity-50"
                >
                  {renaming ? "saving…" : "save"}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingName(false); setNameDraft(c.name || ""); }}
                  disabled={renaming}
                  data-testid={`channel-profile-rename-cancel-${idx}`}
                  className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-50"
                >
                  cancel
                </button>
              </div>
            ) : (
              <>
                <span
                  data-testid={`channel-profile-char-name-${idx}`}
                  className="font-bold text-zinc-100 text-sm"
                >
                  {c.name}
                </span>
                <button
                  type="button"
                  onClick={() => { setNameDraft(c.name || ""); setEditingName(true); }}
                  data-testid={`channel-profile-rename-open-${idx}`}
                  title="Rename this character"
                  aria-label="Rename character"
                  className="text-zinc-500 hover:text-yellow-300 transition-colors"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </>
            )}
            <span
              data-testid={`channel-profile-species-${idx}`}
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300"
            >
              {c.species || <span className="text-zinc-600 lowercase">— species unknown —</span>}
            </span>
            <div className="ml-auto">
              <button
                type="button"
                onClick={onRemoveCharacter}
                disabled={removing}
                data-testid={`channel-profile-char-remove-${idx}`}
                className="font-mono text-[9px] uppercase tracking-[0.25em] text-red-400 hover:text-red-200 border border-red-500/40 hover:border-red-400 px-2 py-0.5 rounded-sm transition-colors disabled:opacity-50"
              >
                {removing ? "removing…" : "remove"}
              </button>
            </div>
          </div>
          <EditableProfileLine
            label="Species"
            value={c.species || ""}
            placeholder="e.g. Shih Tzu dog, white rabbit, gray kitten"
            multiline={false}
            onSave={(v) => saveField("species", v)}
          />
          <EditableProfileLine
            label="Coat / hair"
            value={c.fur_or_hair || ""}
            placeholder="describe coat, fur, or hair"
            onSave={(v) => saveField("fur_or_hair", v)}
          />
          <EditableProfileLine
            label="Face"
            value={c.face || ""}
            placeholder="describe face / expression"
            onSave={(v) => saveField("face", v)}
          />
          <EditableProfileLine
            label="Outfit"
            value={
              c.outfit && c.outfit !== "none — natural fur" ? c.outfit : ""
            }
            placeholder='outfit (or leave blank for "none — natural fur")'
            onSave={(v) => saveField("outfit", v || "none — natural fur")}
          />
          <EditableProfileLine
            label="Accessories"
            value={(c.accessories || []).join(", ")}
            placeholder="comma-separated, e.g. pink bow, scarf"
            multiline={false}
            onSave={(v) => saveField("accessories", v)}
          />
        </div>
      </div>
    </div>
  );
}


function EditableProfileLine({ label, value, placeholder, onSave, multiline = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(value || "");
    setEditing(true);
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };
  const commit = async () => {
    const next = draft.trim();
    if (next === (value || "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // toast already raised by onSave; keep editor open for retry.
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="text-[11px] leading-relaxed group">
        <span className="font-mono uppercase tracking-[0.15em] text-zinc-500">{label}: </span>
        <span
          role="button"
          tabIndex={0}
          onClick={start}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); start(); } }}
          title="Click to edit"
          data-testid={`char-profile-edit-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={(value ? "text-zinc-200" : "text-zinc-600 italic") + " cursor-text border-b border-dashed border-transparent group-hover:border-zinc-700 hover:text-yellow-100 transition-colors"}
        >
          {value || placeholder || "click to add"}
        </span>
      </div>
    );
  }

  const InputTag = multiline ? "textarea" : "input";
  return (
    <div className="text-[11px] leading-relaxed space-y-1">
      <div className="font-mono uppercase tracking-[0.15em] text-zinc-500">{label}:</div>
      <InputTag
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") cancel();
          if (e.key === "Enter") {
            if (!multiline || e.metaKey || e.ctrlKey) {
              e.preventDefault();
              commit();
            }
          }
        }}
        rows={multiline ? 2 : undefined}
        placeholder={placeholder}
        className="w-full bg-black border border-emerald-400/40 rounded-sm px-2 py-1.5 text-xs text-zinc-100 focus:border-emerald-300 focus:outline-none resize-y"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={commit}
          disabled={saving}
          className="font-mono text-[9px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 text-emerald-200 hover:text-emerald-100 px-2 py-0.5 rounded-sm transition-colors disabled:opacity-50"
        >
          {saving ? "saving…" : "save"}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-50"
        >
          cancel
        </button>
        <span className="font-mono text-[9px] text-zinc-600">
          {multiline ? "ctrl+enter to save · esc to cancel" : "enter to save · esc to cancel"}
        </span>
      </div>
    </div>
  );
}


function AddCharacterForm({ handle, existingNames, onAdded }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [species, setSpecies] = useState("");
  const [outfit, setOutfit] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(""); setSpecies(""); setOutfit("");
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error("Character needs a name");
      return;
    }
    if (existingNames.includes(cleanName.toLowerCase())) {
      toast.error(`${cleanName} is already in the cast`);
      return;
    }
    setSaving(true);
    try {
      const res = await addCharacter(handle, {
        name: cleanName,
        species: species.trim(),
        outfit: outfit.trim(),
      });
      toast.success(`${cleanName} added to the cast`);
      onAdded?.(res.character);
      reset();
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const friendly = (
        detail === "character_already_exists" ? "That character is already in the cast"
        : detail === "channel_not_analyzed_yet" ? "Analyze this channel first"
        : detail || "Couldn't add character"
      );
      toast.error(friendly);
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="channel-profile-add-char-open"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] border border-dashed border-emerald-400/40 hover:border-emerald-300 text-emerald-300 hover:text-emerald-100 py-2 rounded-sm transition-colors"
      >
        + Add character
      </button>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="channel-profile-add-char-form"
      className="border border-emerald-400/40 bg-emerald-400/5 rounded-sm p-3 space-y-2"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300">
        // New character
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="space-y-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Name *</span>
          <input
            data-testid="channel-profile-add-char-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mia"
            maxLength={60}
            autoFocus
            className="w-full bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-zinc-100 focus:border-emerald-400/60 focus:outline-none"
          />
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Species</span>
          <input
            data-testid="channel-profile-add-char-species"
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            placeholder="kitten"
            maxLength={100}
            className="w-full bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-zinc-100 focus:border-emerald-400/60 focus:outline-none"
          />
        </label>
        <label className="space-y-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">Outfit</span>
          <input
            data-testid="channel-profile-add-char-outfit"
            value={outfit}
            onChange={(e) => setOutfit(e.target.value)}
            placeholder="purple dress"
            maxLength={200}
            className="w-full bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-xs text-zinc-100 focus:border-emerald-400/60 focus:outline-none"
          />
        </label>
      </div>
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        After adding, click the dashed <span className="text-emerald-300">+ image</span> tile next to the character&apos;s name to upload their reference image.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving || !name.trim()}
          data-testid="channel-profile-add-char-submit"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-emerald-400/60 hover:border-emerald-300 bg-emerald-400/10 hover:bg-emerald-400/20 text-emerald-200 hover:text-emerald-100 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          {saving ? "adding…" : "Add"}
        </button>
        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          disabled={saving}
          data-testid="channel-profile-add-char-cancel"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-300 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}


function ReferenceImageQuickSet({ handle, current, onSaved }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(current || "");
  const [saving, setSaving] = useState(false);

  const onPaste = async () => {
    if (!handle || saving) return;
    setSaving(true);
    try {
      const res = await saveChannelProfile(handle, {
        user_reference_image_url: url.trim(),
      });
      onSaved?.(res.profile);
      toast.success(url.trim()
        ? "Reference image saved — used on next Create Image"
        : "Reference image cleared");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save reference URL");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-zinc-900 pt-2 space-y-1.5">
      <button
        type="button"
        data-testid="channel-profile-set-ref-toggle"
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300 hover:text-cyan-200"
      >
        {current
          ? open ? "[close]" : "[change reference image URL]"
          : open ? "[close]" : "[paste your own reference image URL]"}
      </button>
      {open && (
        <div className="flex flex-wrap gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://… (your ChatGPT-generated reference)"
            data-testid="channel-profile-ref-input"
            className="flex-1 min-w-[260px] bg-black border border-zinc-800 focus:border-cyan-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-[11px] text-zinc-100 font-mono"
          />
          <button
            type="button"
            onClick={onPaste}
            disabled={saving}
            data-testid="channel-profile-ref-save"
            className="font-mono text-[10px] uppercase tracking-[0.25em] bg-cyan-400 text-black hover:bg-cyan-300 font-black px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
          >
            {saving ? "Saving…" : "Use this image"}
          </button>
        </div>
      )}
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        Tip: paste a URL to your own ChatGPT-generated reference of the
        characters. gpt-image-1 will use it as a visual anchor for every
        Create Image / GPT Image render — locking species, fur, and the
        full 3D look directly from the reference.
      </p>
    </div>
  );
}
